import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import useAuthStore from '@/store/useAuthStore'
import useGetBillingSubscription from '@/services/api/billing/useGetBillingSubscription'
import useRequestEarlyAccess, {
  type EarlyAccessSource,
} from '@/services/api/billing/useRequestEarlyAccess'

interface EarlyAccessButtonProps {
  /** Where the click came from — stored with the request. */
  source: EarlyAccessSource
  className?: string
  size?: 'sm' | 'md' | 'lg'
  requestedLabel?: string
}

/**
 * "Request early access" for Pro while it is coming soon: one click, no form.
 * Shows "Requested" on every device once the user has asked (the status comes
 * from the billing payload, per user).
 */
export default function EarlyAccessButton({
  source,
  className,
  size,
  requestedLabel = "Requested — we'll be in touch",
}: EarlyAccessButtonProps) {
  const currentTeamId = useAuthStore((state) => state.currentTeamId)
  const { data } = useGetBillingSubscription({ teamId: currentTeamId })
  const request = useRequestEarlyAccess()

  const requested =
    !!data?.early_access?.status || request.isPending || request.isSuccess

  if (requested) {
    return (
      <Button size={size} variant="outline" disabled className={className}>
        <Check className="mr-2 size-4 shrink-0" />
        {requestedLabel}
      </Button>
    )
  }

  return (
    <Button
      size={size}
      className={className}
      onClick={() => request.mutate(source)}
    >
      Request early access
    </Button>
  )
}
